/**
 * IngestBreadcrumb — sticky context header for ingest pipeline pages.
 *
 * Tells the user where they are inside the pipeline — Upload, Review,
 * or Item preview — and provides up-tree navigation. Without this,
 * deep links like /ingest/batches/X/items/Y land users on a screen
 * with no indicator of how to back out.
 */

import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { cn } from '../../lib/utils'

export interface BreadcrumbStep {
  label: string
  /** Absolute path. Omit for the current step (renders as plain text). */
  to?: string
}

interface Props {
  steps: BreadcrumbStep[]
  /** Optional right-aligned element (action buttons, status pill). */
  trailing?: React.ReactNode
}

export function IngestBreadcrumb({ steps, trailing }: Props) {
  const isDark = useAppStore((s) => s.theme === 'dark')

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm',
        isDark ? 'border-white/[0.06] bg-surface-1/40' : 'border-neutral-200 bg-white',
      )}
    >
      <ol className="flex items-center gap-1 min-w-0">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1
          return (
            <li key={`${step.label}-${idx}`} className="flex items-center gap-1 min-w-0">
              {step.to && !isLast ? (
                <Link
                  to={step.to}
                  className={cn(
                    'truncate rounded px-1.5 py-0.5 transition-colors',
                    isDark
                      ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200'
                      : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800',
                  )}
                >
                  {step.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn(
                    'truncate px-1.5 py-0.5 font-medium',
                    isDark ? 'text-neutral-100' : 'text-neutral-900',
                  )}
                  title={step.label}
                >
                  {step.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight
                  size={14}
                  aria-hidden="true"
                  className={isDark ? 'text-neutral-600' : 'text-neutral-400'}
                />
              )}
            </li>
          )
        })}
      </ol>
      {trailing && <div className="flex items-center gap-2 shrink-0">{trailing}</div>}
    </nav>
  )
}
